// Phase 4 of the harvest & labour plan: budgets, crop estimates and insights
// (/api/field-work/budgets, /reports/budget, /crop-estimates, /insights).
// Money keys are absent when `includes_costs` is false (not a Farm Jobs admin).

export interface BudgetLine {
  id?: number;
  farm_id: number | null;
  block_id: number | null;
  cultivar_area_id: number | null;
  activity_type_id: number | null;
  hours: number | null;
  labour_cost?: number | null;
  bins: number | null;
  tonnes: number | null;
  notes?: string | null;
  farm_name?: string | null;
  block_name?: string | null;
  cultivar_area_name?: string | null;
  activity_type_name?: string | null;
  area_hectares?: number | null;
}

export interface SeasonInfo {
  season_year: number;
  current_season: number;
  season_start?: string;
  season_end?: string;
  includes_costs?: boolean;
}

export interface Budget extends SeasonInfo {
  notes: string | null;
  exists: boolean;
  lines: BudgetLine[];
}

export interface Actuals {
  hours: number;
  cost?: number;
  bins: number;
  tonnes: number;
  unrated_hours: number;
}

export interface BudgetReportRow extends BudgetLine {
  actual: Actuals;
  forecast: { method: 'per_bin' | 'budget'; hours: number; cost?: number };
  hours_used_pct: number | null;
  cost_used_pct?: number | null;
  bins_done_pct: number | null;
  cost_variance?: number | null;
  hours_variance: number | null;
}

export interface BudgetReport extends SeasonInfo {
  actuals_to: string;
  currency: string;
  exists: boolean;
  rows: BudgetReportRow[];
  unbudgeted: Actuals;
  totals: {
    budget: { hours: number; labour_cost?: number; bins: number; tonnes: number };
    actual: { hours: number; cost?: number; bins: number; tonnes: number };
    forecast: { hours: number; cost?: number };
  };
}

export interface CopyBudgetInput {
  source: 'actuals' | 'budget';
  uplift_pct: number;
  replace: boolean;
}

export interface ProgressRow {
  cultivar_area_id: number;
  cultivar_area_name: string;
  farm_id: number;
  farm_name: string;
  block_id: number | null;
  block_name: string | null;
  cultivar_name: string | null;
  area_hectares: number | null;
  tree_count: number | null;
  est_bins: number | null;
  est_tonnes: number | null;
  estimate_notes: string | null;
  bins: number;
  tonnes: number;
  weighed_tonnes: number;
  bins_today: number;
  first_pick: string | null;
  last_pick: string | null;
  last_season_bins: number | null;
  last_season_tonnes: number | null;
  /** From yield history (harvestFeedbackService.suggestEstimate). */
  suggested_bins: number | null;
  suggestion_basis: 'young_trend' | 'average' | 'last_season' | null;
  window_start: string | null;
  window_end: string | null;
  window_status: string | null;
  bins_per_ha: number | null;
  est_bins_per_ha: number | null;
  progress_pct: number | null;
  progress_basis: 'bins' | 'tonnes' | null;
  remaining_bins: number | null;
  bins_per_pick_day: number | null;
  days_to_finish: number | null;
  days_left_in_window: number | null;
  behind_window: boolean;
}

export interface HarvestProgress extends SeasonInfo {
  today: string;
  rows: ProgressRow[];
  unplaced_bins: number;
  totals: { est_bins: number; est_tonnes: number; bins: number; bins_today: number; tonnes: number; progress_pct: number | null };
}

export interface EstimateInput {
  cultivar_area_id: number;
  est_bins: number | null;
  est_tonnes: number | null;
  notes?: string | null;
}

export interface Kpis extends Actuals {
  area_hectares: number | null;
  tree_count: number | null;
  hours_per_ha: number | null;
  cost_per_ha?: number | null;
  cost_per_tree?: number | null;
  bins_per_ha: number | null;
  tonnes_per_ha: number | null;
  picking_hours: number;
  bins_per_picking_hour: number | null;
  cost_per_bin?: number | null;
  cost_per_tonne?: number | null;
}

export interface Insights extends SeasonInfo {
  actuals_to: string;
  currency: string;
  tax_name: string;
  totals: Kpis;
  by_activity: { activity_type_id: number | null; name: string; hours: number; cost?: number; bins: number;
    hours_per_ha: number | null; cost_per_ha?: number | null; cost_per_tree?: number | null }[];
  by_provider: { labour_provider_id: number | null; name: string; hours: number; cost?: number; bins: number; tonnes: number;
    bins_per_picking_hour: number | null; cost_per_bin?: number | null; cost_per_tonne?: number | null;
    people: number; packed_kg: number | null; reject_pct: number | null }[];
  by_cultivar_area: (Kpis & { cultivar_area_id: number | null; name: string; block_name: string | null;
    farm_name: string | null; cultivar_name: string | null })[];
}

/** "2026–27" for a season that crosses the new year, "2026" for one that starts in January. */
export function seasonLabel(year: number, startMonth = 7): string {
  return startMonth === 1 ? String(year) : `${year}–${String((year + 1) % 100).padStart(2, '0')}`;
}
